/**
 * Back in Stock button component
 * Triggers modal opening and delegates population to the form
 * Autonomous custom element wrapping an inner <button> for Safari compatibility
 */
class BISButton extends HTMLElement {
  connectedCallback() {
    const button = this.querySelector('button');
    if (button) {
      button.addEventListener('click', this.handleClick.bind(this));
    }
  }

  handleClick(event) {
    event.preventDefault();

    // Find the global BIS form (should only be one on the page)
    const bisForm = document.querySelector('bis-global-form');
    if (!bisForm) {
      console.error('BIS form not found on page');
      return;
    }

    // Gather variant data from wrapper's data attributes
    const variantData = {
      variantId: this.dataset.variantId,
      variantTitle: this.dataset.variantTitle
    };

    // Populate the form with variant data
    bisForm.populate(variantData);

    // Find and open the modal containing the form
    const modal = bisForm.closest('modal-dialog');
    if (modal) {
      modal.show(this);
    } else {
      console.error('BIS modal not found');
    }
  }
}

/**
 * Back in Stock global form component
 * Handles form population and submission for BIS notifications
 */
class BISGlobalForm extends HTMLElement {
  constructor() {
    super();
    this.variantData = null;
  }

  /**
   * Web Component lifecycle - called when element is added to DOM
   * Sets up modal close listener to clear form content
   */
  connectedCallback() {
    const modal = this.closest('modal-dialog');
    if (modal) {
      modal.addEventListener('modal-close', this.handleModalClose.bind(this));
    }
  }

  /**
   * Clears form content when modal closes to ensure fresh state on reopen
   */
  handleModalClose() {
    this.innerHTML = '';
    this.variantData = null;
  }

  /**
   * Populates the form from the template with variant data
   * @param {Object} data - Variant data from the BIS button
   */
  populate(data) {
    this.variantData = data;

    // Get the template and clone its content
    const template = document.getElementById('bis-form-template');
    if (!template) {
      console.error('BIS form template not found');
      return;
    }

    // Clear existing content and inject cloned template
    this.innerHTML = '';
    const clonedContent = template.content.cloneNode(true);

    // Populate variant ID hidden input
    const variantIdInput = clonedContent.querySelector('[name="variant_id"]');
    if (variantIdInput) variantIdInput.value = data.variantId;

    // Replace variant title placeholder in description
    const descriptionEl = clonedContent.querySelector('[data-description-template]');
    if (descriptionEl) {
      const template = descriptionEl.dataset.descriptionTemplate;
      descriptionEl.innerHTML = template.replace('%{variant_title}', data.variantTitle);
    }

    // Inject populated content into the DOM
    this.appendChild(clonedContent);

    // Attach form submit handler
    const form = this.querySelector('form');
    if (form) {
      form.addEventListener('submit', this.handleSubmit.bind(this));
    }
  }

  /**
   * Handles form submission
   * @param {Event} e - Submit event
   */
  handleSubmit(e) {
    e.preventDefault();

    // Clear any existing errors
    this.hideError();

    const email = this.querySelector('#bis-email').value.trim();
    if (!email) return;

    const companyId = window.themeSettings?.klaviyoSiteId;

    if (!companyId) {
      this.showError('Configuration error. Please contact support.');
      return;
    }

    this.submitToKlaviyo(email, companyId);
  }

  /**
   * Submits the email to Klaviyo using modern client-side API
   * @param {string} email - User's email address
   * @param {string} companyId - Klaviyo public API key (site ID)
   */
  async submitToKlaviyo(email, companyId) {
    const submitButton = this.querySelector('[type="submit"]');

    // Cache original text before changing it
    if (submitButton && !submitButton.dataset.originalText) {
      submitButton.dataset.originalText = submitButton.textContent;
    }

    // Set loading state
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.textContent = 'Submitting...';
    }

    // Build the JSON payload for Klaviyo's client-side API
    const payload = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email
              }
            }
          },
          channels: ['EMAIL']
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              // Shopify variant ID format: $shopify:::$default:::VARIANT_ID
              id: `$shopify:::$default:::${this.variantData?.variantId || ''}`
            }
          }
        }
      }
    };

    try {
      const response = await fetch(
        `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${companyId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'revision': '2024-06-15'
          },
          body: JSON.stringify(payload)
        }
      );

      // Klaviyo returns 202 for successful subscription
      if (response.status === 202) {
        this.showSuccess();
        return;
      }

      // Handle error responses
      let errorMessage = 'Sorry! There was an error. Please try again.';

      if (response.status === 400) {
        try {
          const errorData = await response.json();
          if (errorData?.errors?.[0]?.detail) {
            errorMessage = errorData.errors[0].detail;
          }
        } catch (e) {
          // JSON parsing failed, use default message
        }
      } else if (response.status === 404) {
        errorMessage = 'Product variant not found. Please contact support.';
      }

      throw new Error(errorMessage);

    } catch (error) {
      // Reset button state
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.textContent = submitButton.dataset.originalText || 'Notify Me';
      }

      // Show error message
      const errorEl = this.querySelector('[data-bis-error]');
      const userMessage = errorEl?.dataset.errorMessage || 'Sorry! There was an error. Please try again.';
      this.showError(userMessage);
    }
  }

  /**
   * Shows inline error message below email input
   * @param {string} message - Error message to display
   */
  showError(message) {
    const errorEl = this.querySelector('[data-bis-error]');
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.classList.remove('hidden');
      errorEl.removeAttribute('hidden');
    }
  }

  /**
   * Hides inline error message
   */
  hideError() {
    const errorEl = this.querySelector('[data-bis-error]');
    if (errorEl) {
      errorEl.classList.add('hidden');
      errorEl.setAttribute('hidden', '');
      errorEl.textContent = '';
    }
  }

  /**
   * Hides form and shows success message from template
   */
  showSuccess() {
    const form = this.querySelector('form');
    const successEl = this.querySelector('[data-bis-success]');

    if (form) {
      form.classList.add('hidden');
      form.setAttribute('hidden', '');
    }

    if (successEl) {
      successEl.classList.remove('hidden');
      successEl.removeAttribute('hidden');
    }
  }
}

window.customElements.define('bis-button', BISButton);
window.customElements.define('bis-global-form', BISGlobalForm);
